import { useCallback, useEffect, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import {
  CellValueChangedEvent,
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent,
  MenuItemDef,
} from 'ag-grid-community'
import { styled } from '@mui/material'
import _ from 'lodash'
import { useSelector } from 'react-redux'
import { PageArea, PageProps } from '..'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { pageComponent } from '../../higher-order-components/pageComponent'
import Loading from '../../components/process-state-notifications/Loading'
import ReloadButton from '../../containers/BulkSheetView/components/header/ReloadButton'
import SavePopper from '../../containers/BulkSheetView/components/header/SaveButtonArea'
import FilterButton from '../../containers/BulkSheetView/components/header/FilterButton'
import SortButton from '../../containers/BulkSheetView/components/header/SortButton'
import ColumnSettingButton from '../../containers/BulkSheetView/components/header/ColumnSettingButton'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import CancelConfirmDialog from '../../components/dialogs/CancelConfirmDialog'
import { DeleteRowConfirmationDialog } from '../../containers/BulkSheetView/components/dialog/DeleteRowConfirmationDialog'
import { useBulkSheetState } from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { useColumnState } from '../../containers/BulkSheetView/hooks/columnState/useColumnState'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import { validateNodes } from '../../containers/BulkSheetView/lib/validation'
import { getRowNumber } from '../../containers/BulkSheetView/lib/gridApi'
import store, { AllState } from '../../../store'
import { requireSave } from '../../../store/requiredSaveData'
import { addGlobalMessage, MessageLevel } from '../../../store/messages'
import { intl } from '../../../i18n'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'
import { useProgramGridOptions } from './gridOptions'
import { useProgramData } from './hooks/useProgramData'
import { useProgramMasterData } from './hooks/useProgramMasterData'
import { ProgramRow } from './model/programRow'
import { isProgramEditable } from './model/programPermission'

const Programs = ({ uuid: functionUuid }: PageProps) => {
  const gridRef = useRef<AgGridReact<ProgramRow>>(null)
  const gridApiRef = useRef<GridApi>()
  const bulkSheetRef = useRef<HTMLDivElement>(null)
  const [saving, setSaving] = useState(false)
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteTargetUuids, setDeleteTargetUuids] = useState<string[]>([])
  const [, setGridReady] = useState(false)

  const { data, loading, fetchPrograms, hasChanged, save, addRow, deleteRows } =
    useProgramData()
  const masterData = useProgramMasterData()
  const currentUserUuid = useSelector(
    (state: AllState) => state.user.user?.uuid
  )
  const isAdmin = useSelector(
    (state: AllState) => state.user.user?.hasAllPrivileges ?? false
  )
  const gridOptions = useProgramGridOptions(masterData, {
    currentUserUuid,
    isAdmin,
  })

  const bulkSheetState = useBulkSheetState(
    functionUuid,
    UiStateKey.ProgramColumnAndFilterState
  )
  const columnState = useColumnState({
    gridApi: gridApiRef.current,
    autoGroupColumnDef: gridOptions.autoGroupColumnDef,
    bulkSheetState,
  })
  const columnSetting = useColumnSetting()

  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<ProgramRow>) => {
      if (event.data && !event.data.added) {
        event.data.edited = true
      }
      store.dispatch(requireSave())
    },
    []
  )

  const handleDeleteRequest = useCallback((uuids: string[]) => {
    setDeleteTargetUuids(uuids)
    setDeleteDialogOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(() => {
    deleteRows(deleteTargetUuids)
    setDeleteDialogOpen(false)
    setDeleteTargetUuids([])
  }, [deleteTargetUuids, deleteRows])

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false)
    setDeleteTargetUuids([])
  }, [])

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const selectedNodes = params.api.getSelectedNodes()
      const targetNodes =
        selectedNodes.length > 0
          ? selectedNodes
          : params.node
            ? [params.node]
            : []
      const targetRows = targetNodes
        .map(node => node.data)
        .filter((row): row is ProgramRow => !!row)
      const targetUuids = targetRows.map(row => row.uuid)
      const canDelete =
        targetRows.length > 0 &&
        targetRows.every(row =>
          isProgramEditable(row, currentUserUuid, isAdmin)
        )
      return [
        {
          name: intl.formatMessage({ id: 'programList.contextMenu.addRow' }),
          action: () => addRow(),
        },
        {
          name: intl.formatMessage({ id: 'programList.contextMenu.deleteRow' }),
          disabled: !canDelete,
          action: () => handleDeleteRequest(targetUuids),
        },
      ]
    },
    [addRow, handleDeleteRequest, currentUserUuid, isAdmin]
  )

  const getDeleteRowLabels = useCallback(
    () =>
      deleteTargetUuids
        .map(uuid => data.find(row => row.uuid === uuid)?.displayName ?? '')
        .filter(displayName => displayName !== ''),
    [deleteTargetUuids, data]
  )

  const handleReload = useCallback(() => {
    if (hasChanged()) {
      setCancelConfirmOpen(true)
    } else {
      fetchPrograms()
    }
  }, [hasChanged, fetchPrograms])

  const onConfirmedReload = useCallback(() => {
    setCancelConfirmOpen(false)
    fetchPrograms()
  }, [fetchPrograms])

  const onSubmit = useCallback(async () => {
    gridApiRef.current?.stopEditing()
    const targetUuids = data
      .filter(row => row.added || row.edited)
      .map(row => row.uuid)
    const errors = validateNodes({
      gridApi: gridApiRef.current,
      uuids: targetUuids,
      extraValidate: (colId, node, colDef) => {
        const validate = colDef.cellRendererParams?.validate
        if (!validate || !node || !colId || !gridApiRef.current) return
        const value = gridApiRef.current.getCellValue({
          colKey: colId,
          rowNode: node,
        })
        return validate(value, {
          node,
          data: node.data,
          colDef,
          colId,
          api: gridApiRef.current,
        })
      },
    })
    if (errors.hasMessage()) {
      store.dispatch(
        addGlobalMessage({
          type: MessageLevel.WARN,
          title: intl.formatMessage({ id: 'programList.validation.title' }),
          text: errors.toMessage(id => {
            const node = gridApiRef.current?.getRowNode(id)
            return node ? getRowNumber(node).toString() : ''
          }),
        })
      )
      return
    }
    try {
      setSaving(true)
      const response = await save()
      if (!response.hasError && !response.hasWarning) {
        store.dispatch(
          addGlobalMessage({
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({ id: 'registration.complete' }),
          })
        )
        await fetchPrograms()
      }
    } finally {
      setSaving(false)
    }
  }, [data, save, fetchPrograms])

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      gridApiRef.current = event.api
      setGridReady(true)
      if (!_.isEmpty(bulkSheetState.columnState)) {
        event.api.applyColumnState({
          state: bulkSheetState.columnState,
          applyOrder: true,
        })
      }
    },
    [bulkSheetState.columnState]
  )

  const onFirstDataRendered = useCallback(() => {
    if (!gridApiRef.current) return
    if (!_.isEmpty(bulkSheetState.filterState)) {
      gridApiRef.current.setFilterModel(bulkSheetState.filterState ?? null)
    }
    bulkSheetState.finishRestoring()
  }, [bulkSheetState.filterState, bulkSheetState.finishRestoring])

  useEffect(() => {
    if (!bulkSheetState.initialized) return
    fetchPrograms()
  }, [bulkSheetState.initialized, fetchPrograms])

  useKeyBind(
    [{ key: KEY_SAVE, fn: onSubmit, stopDefaultBehavior: true }],
    [onSubmit]
  )

  // currentUserUuid が未確定のまま行を描画すると、遷移アイコン(マネージャー/コラボレーター判定)や
  // 編集可否が誤って評価され、後からユーザー情報が届いても AgGrid はセルを再描画しないため
  // アイコンが非表示のままになる。ユーザー確定を待ってから描画する。
  const ready =
    masterData.loaded && bulkSheetState.initialized && !!currentUserUuid

  return (
    <PageArea>
      <Header>
        <SavePopper loading={saving} onSubmit={onSubmit} />
        <ReloadButton onReload={handleReload} />
        <FilterButton
          filteredColumns={columnState.filteredColumns}
          onDeleteFilteredColumn={columnState.onDeleteFilteredColumn}
          resetFilters={columnState.resetFilters}
        />
        <SortButton
          onDeleteSortedColumn={columnState.onDeleteSortedColumn}
          onDeleteSortedAllColumns={columnState.onDeleteSortedAllColumns}
          onChangeSortColumnState={columnState.onChangeSortColumnState}
          sortColumnsState={columnState.sortColumnsState}
        />
        <ColumnSettingButton
          onClick={columnSetting.toggle}
          open={columnSetting.isOpen}
        />
      </Header>
      {ready && (
        <BulkSheetView
          ref={bulkSheetRef}
          gridRef={gridRef}
          gridOptions={gridOptions}
          rowData={data}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered}
          onCellValueChanged={onCellValueChanged}
          getContextMenuItems={getContextMenuItems}
          onColumnVisible={columnState.onColumnVisible}
          onColumnResized={columnState.rememberColumnState}
          onColumnMoved={columnState.rememberColumnState}
          onFilterChanged={columnState.onFilterChanged}
          onSortChanged={columnState.onSortChanged}
        />
      )}
      <Loading
        isLoading={!ready || loading || saving}
        elem={bulkSheetRef.current}
      />
      <CancelConfirmDialog
        open={cancelConfirmOpen}
        onConfirm={onConfirmedReload}
        onClose={() => setCancelConfirmOpen(false)}
      />
      {deleteDialogOpen && (
        <DeleteRowConfirmationDialog
          target={getDeleteRowLabels()}
          onConfirm={handleDeleteConfirm}
          onClose={handleDeleteCancel}
        />
      )}
      {columnSetting.isOpen && (
        <ColumnSettingPopper
          anchorEl={columnSetting.anchorEl}
          open={columnSetting.isOpen}
          close={columnSetting.close}
          gridApi={gridApiRef.current || undefined}
          height={bulkSheetRef.current?.offsetHeight}
          openSavedUiStateDialog={undefined}
          initializeColumnState={columnState.resetColumnState}
          applicationFunctionUuid={functionUuid}
          uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
          columnState={bulkSheetState.columnState}
          offset={120}
        />
      )}
    </PageArea>
  )
}

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  margin: '10px 10px 5px 10px',
  overflowX: 'auto',
  overflowY: 'hidden',
  minHeight: '47px',
})

export default pageComponent(Programs)
